<template>
  <div class="sharer-rate-tiers">

    <!-- Cross-tier contiguity error banner -->
    <el-alert
      v-if="contiguityError"
      type="error"
      :closable="false"
      show-icon
      :description="contiguityError"
      style="margin-bottom: 16px;">
    </el-alert>

    <!-- Tier cards -->
    <sharer-rate-tier-card
      v-for="(tier, index) in tiers"
      :key="tier.seqNo"
      :tier="tier"
      :tier-index="index"
      :max-months="maxMonths"
      :to-sharer="toSharer"
      :disabled="disabled"
      :template-mode="templateMode"
      :from-month-error="validationErrors[index] && validationErrors[index].fromMonth || null"
      :range-error="validationErrors[index] && validationErrors[index].range || null"
      :rate1-error="templateMode ? null : (validationErrors[index] && validationErrors[index].rate1 || null)"
      @update:tier="onTierUpdate(index, $event)"
      @remove="onRemove(index)">
    </sharer-rate-tier-card>

    <!-- Add tier affordance -->
    <el-tooltip
      :content="tiers.length >= MAX_TIERS ? 'Maximum 10 tiers' : ''"
      :disabled="tiers.length < MAX_TIERS"
      placement="top">
      <span style="display:block;">
        <el-button
          type="primary"
          plain
          icon="el-icon-plus"
          class="add-tier-btn"
          :disabled="disabled || tiers.length >= MAX_TIERS"
          @click="addTier">
          Add tier
        </el-button>
      </span>
    </el-tooltip>

  </div>
</template>

<script>
import SharerRateTierCard from './SharerRateTierCard'

var MAX_TIERS = 10

function blankTier (seqNo, fromMonth, toMonth) {
  return {
    seqNo: seqNo,
    fromMonth: fromMonth,
    toMonth: toMonth,
    telco1: 0,
    telco2: 0,
    telco3: 0,
    telco4: 0,
    telco5: 0,
    telco6: 0,
    telco7: 0,
    telco8: 0,
    telco9: 0,
    telco10: 0,
    mwRate: 0,
    rruRate: 0,
    rruSet: 0
  }
}

// Template-mode blank tier: rate fields null (not 0) so the backend persists
// them as null and the UI shows the "optional" placeholder.
function blankTemplateTier (seqNo, fromMonth, toMonth) {
  return {
    seqNo: seqNo,
    fromMonth: fromMonth,
    toMonth: toMonth,
    telco1: null,
    telco2: null,
    telco3: null,
    telco4: null,
    telco5: null,
    telco6: null,
    telco7: null,
    telco8: null,
    telco9: null,
    telco10: null,
    mwRate: null,
    rruRate: null,
    rruSet: null
  }
}

export default {
  name: 'sharer-rate-tiers',

  components: {
    SharerRateTierCard
  },

  model: {
    prop: 'tiers',
    event: 'input'
  },

  props: {
    tiers: {
      type: Array,
      required: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    maxMonths: {
      type: Number,
      default: 300
    },
    // Number of Sharer Rate inputs to render per tier (mirrors the parent rate's
    // "To Sharer" value — origin/main rendered exactly this many rate fields).
    toSharer: {
      type: [Number, String],
      default: 10
    },
    // Validation errors: array of { fromMonth, range, rate1 } indexed by tier
    validationErrors: {
      type: Array,
      default: function () { return [] }
    },
    // Template mode: relaxes rate1 validation, null-seeds new tiers, always 10 rate fields
    templateMode: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      MAX_TIERS: MAX_TIERS
    }
  },

  computed: {
    isValid () {
      if (!this.tiers || this.tiers.length === 0) return false
      if (this.tiers.length > MAX_TIERS) return false

      // Tier 1 must start at month 0
      if (this.tiers[0].fromMonth !== 0) return false

      for (var i = 0; i < this.tiers.length; i++) {
        var t = this.tiers[i]

        // toMonth must be greater than fromMonth
        if (t.toMonth <= t.fromMonth) return false

        // Contiguity: each tier's fromMonth must equal previous tier's toMonth
        if (i > 0 && t.fromMonth !== this.tiers[i - 1].toMonth) return false

        // Rate #1 required when other rates set (relaxed in template mode)
        if (!this.templateMode && !this.rate1Valid(t)) return false
      }

      return true
    },

    contiguityError () {
      if (!this.tiers || this.tiers.length < 2) return null
      for (var i = 1; i < this.tiers.length; i++) {
        var prev = this.tiers[i - 1]
        var curr = this.tiers[i]
        if (curr.fromMonth !== prev.toMonth) {
          return 'Tiers must be contiguous. Tier ' + i + ' ends at month ' + prev.toMonth + ' but Tier ' + (i + 1) + ' starts at month ' + curr.fromMonth + '.'
        }
      }
      return null
    }
  },

  methods: {
    rate1Valid (tier) {
      var hasOtherRates = false
      for (var i = 2; i <= 10; i++) {
        if (Number(tier['telco' + i]) !== 0) {
          hasOtherRates = true
          break
        }
      }
      if (!hasOtherRates && Number(tier.mwRate) !== 0) hasOtherRates = true
      if (!hasOtherRates && Number(tier.rruRate) !== 0) hasOtherRates = true

      if (hasOtherRates && (!tier.telco1 || Number(tier.telco1) === 0)) {
        return false
      }
      return true
    },

    addTier () {
      if (this.tiers.length >= MAX_TIERS) return

      var prev = this.tiers[this.tiers.length - 1]
      var fromMonth = prev.toMonth
      var toMonth = Math.min(fromMonth + 12, this.maxMonths)
      var newTier = this.templateMode
        ? blankTemplateTier(this.tiers.length + 1, fromMonth, toMonth)
        : blankTier(this.tiers.length + 1, fromMonth, toMonth)

      var updated = this.tiers.concat([newTier])
      this.$emit('input', updated)
    },

    onRemove (index) {
      if (index === 0) return // Never remove tier 1
      if (this.tiers.length <= 1) return // Always keep at least 1

      var tier = this.tiers[index]
      var hasData = this.tierHasData(tier)

      if (hasData) {
        this.$confirm('Remove this tier? Its rates will be discarded.', 'Remove this tier?', {
          confirmButtonText: 'Remove',
          cancelButtonText: 'Cancel',
          type: 'warning'
        }).then(() => {
          this.removeTier(index)
        }).catch(function () {
          // cancelled — do nothing
        })
      } else {
        this.removeTier(index)
      }
    },

    removeTier (index) {
      var updated = this.tiers.slice()
      updated.splice(index, 1)
      updated = this.rechain(updated)
      this.$emit('input', updated)
    },

    tierHasData (tier) {
      // Number(null) === 0, so null rates count as no-data (intended:
      // all-null template tiers remove without $confirm).
      for (var i = 1; i <= 10; i++) {
        if (Number(tier['telco' + i]) !== 0) return true
      }
      if (Number(tier.mwRate) !== 0) return true
      if (Number(tier.rruRate) !== 0) return true
      return false
    },

    onTierUpdate (index, updatedTier) {
      var updated = this.tiers.slice()
      updated[index] = Object.assign({}, updatedTier)
      // Rechain: if toMonth changed, update next tier's fromMonth
      updated = this.rechain(updated)
      this.$emit('input', updated)
    },

    rechain (tiers) {
      if (!tiers || tiers.length === 0) return tiers
      // Tier 1 always starts at 0
      tiers[0] = Object.assign({}, tiers[0], { fromMonth: 0, seqNo: 1 })
      for (var i = 1; i < tiers.length; i++) {
        tiers[i] = Object.assign({}, tiers[i], {
          fromMonth: tiers[i - 1].toMonth,
          seqNo: i + 1
        })
      }
      return tiers
    },

    // Expose for parent — called by SharerRateDetails before save
    validate () {
      return this.isValid
    }
  }
}
</script>

<style scoped>
.sharer-rate-tiers {
  margin-top: 8px;
}

.add-tier-btn {
  width: 100%;
  border-style: dashed;
  margin-top: 4px;
}
</style>
